import {
  NormalButton,
  ConfirmModal,
  NoDataComponent,
  SkeletonGradientBackground,
  TableComponent,
  CustomButton,
  CustomTooltip,
} from '@/shared/ui/components';
import { DisclaimerModal, STUDY_STATUS } from '@/entities/study';
import { facilitySelection } from '@/entities/facility';
import {
  CloseIcon,
  closeWhiteIcon,
  lightbulbIcon,
  restoreIcon,
  studyTableIcon,
} from '@/shared/ui/assets';

import { auth, convertSortOrder, renderSortIcon, formatPhoneNumber } from '@/shared/lib';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Checkbox, Space, Spin, Switch } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { forwardRef } from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { AssignDeviceDrawer } from '@/pages/study-management/@x/studies-list';
import {
  cellFormatter,
  deviceIdFormatter,
  durationFormatter,
  etaReportDateFormatter,
  initDurationFormatter,
  patientNameFormatter,
  patientReturnVisitFormatter,
  stopTimeFormatter,
  studyFidFormatter,
  studyTypeFormatter,
} from '../../lib/studiesTable';
import ConfirmActivityModal from './confirmActivityModal/confirmActivityModal';
import SolutionsDrawer from './drawers/SolutionsDrawer';
import BiodirectInfoDrawer from './drawers/biodirectInfoDrawer/biodirectInfoDrawer';
import MoreOptionsPopover from './moreOptionsPopover/moreOptionsPopover';
import type { FormattedStudy, StudiesTableProps, TableColumn } from './studiesTable.types';
import StudyCard from './studyCard/studyCard';
import CommonQuestionsDrawer from './commonQuestionsDrawer/CommonQuestionsDrawer';
import './style.scss';
import { useStudiesTable } from './useStudiesTable';
import ViewLiveEcgModal from './viewLiveEcgModal/viewLiveEcgModal';
import UnassignedStudyCard from './unassignedStudyCard/unassignedStudyCard';
import UploadStatusModal from './uploadStatusModal/uploadStatusModal';

/**
 * Renders one studies tab - ongoing, unassigned, flagged, completed or deleted - as a card list or a paged table together with its modals and drawers. State and handlers come from useStudiesTable; abort, stop, reference-code, live-ECG, upload-status and delete confirmations are mounted per tab, the search-match and selected-studies bars sit above the list, and and the body switches between StudyCard, UnassignedStudyCard, TableComponent fed by generateColumns, or NoDataComponent.
 *
 * @returns The studies tab markup, or false while the tab is inactive with no loaded data.
 */
const StudiesTable = forwardRef(
  ({
    isDeleted,
    isCompleted,
    isEndOfDuration,
    isOngoing,
    isUnassigned,
    isFlaggedStudiesTab,
    selectedFacility,
    selectedTypeSeePatients,
    handleFetchOngoingCount,
    onSelectStudy,
    onSelectAllStudies,
    isActive,
    supportFacilities,
    toggleDisplayConfirmDeleteStudiesModal,
    checkedStudyIds,
    onClickClearSelectStudies,
    searchValue,
    searchByValue,
    multiStudyFid,
    isOpenConfirmDeleteStudiesModal,
    deleteStudies,
    isDeletingStudies,
    studyType,
    handleClickClear,
    toggleDisplayDrawer: toggleDisplayDrawerProp,
    type,
  }: StudiesTableProps) => {
    const {
      navigate,
      filterObject,
      viewLiveEcgModalRef,
      studyCardRefs,
      state,
      setState,
      hasSentRefCode,
      onUpdateMarkAddressed,
      onClickCopyStudy,
      handleAssignDeviceClick,
      handleAssignDeviceDrawerClick,
      displayConfirmActivityModal,
      displayStopLiveEcgModal,
      displayViewLiveEcg,
      onClickConfirmActivityModal,
      handleStopStreaming,
      handleResumeStreaming,
      onClickRestoreButton,
      useBottomScrollListener,
      onClickGetECG,
      handleTableChange,
      toggleDisplaySolutionsDrawer,
      toggleDisplayDisclaimerModal,
      toggleDisplayUploadStatusModal,
      resendReferenceCode,
      toggleDisplayResendModal,
      handleClickCommonQuestions,
      onChangeCheckbox,
      onChangeCheckboxAbortedStudy,
      onClickConfirm,
      toggleDisplayDrawer,
      onClickClear,
      toggleDisplayConfirmStopStudyModal,
      stopStudy,
      handleAbortClick,
      handleCancelClick,
      handleRightButtonClick,
      handleGetLastEvaluationEvent,
      shouldDisplayStudiesMatch,
      shouldDisplaySelectedStudies,
    } = useStudiesTable({
      isDeleted,
      isCompleted,
      isEndOfDuration,
      isOngoing,
      isUnassigned,
      isFlaggedStudiesTab,
      selectedFacility,
      selectedTypeSeePatients,
      handleFetchOngoingCount,
      isActive,
      supportFacilities,
      checkedStudyIds,
      searchValue,
      searchByValue,
      multiStudyFid,
      studyType,
      handleClickClear,
      type,
    });

    /**
     * Builds the confirmation body asking where a study's reference code will be sent. Returns an empty string when no study is staged in state.displayedResendRefCodeStudy, otherwise renders the patient phone through formatPhoneNumber together with the email for US patients (phone alone when there is no email) or only the email for other countries, wording the sentence as resend or send from hasSentRefCode.
     *
     * @returns The confirmation element, or an empty string when no study is staged.
     */
    const generateResendReferenceCodeQuestion = () => {
      if (_.isEmpty(state.displayedResendRefCodeStudy)) {
        return '';
      }

      let address;
      const { patient } = state.displayedResendRefCodeStudy;
      const country = patient?.contact?.country;
      const email = patient?.email;
      const phone = patient?.contact?.phone1;
      if (country === 'US') {
        if (email) {
          address = (
            <>
              <b>{formatPhoneNumber(phone)}</b> and <b>{email}</b>
            </>
          );
        } else {
          address = <b>{formatPhoneNumber(phone)}</b>;
        }
      } else {
        address = <b>{email}</b>;
      }
      return (
        <div>
          {`Are you sure you want to ${hasSentRefCode ? 'resend' : 'send'} the reference code to`}{' '}
          {address}?
        </div>
      );
    };
    /**
     * Renders the trailing action cell of a studies-table row. Shows a one-line skeleton until state.isLoadedData, then on the completed tab a MoreOptionsPopover plus a button navigating to `/studies/{id}`, and otherwise a restore button calling onClickRestoreButton with the row id and studyFid.
     *
     * @param _ - The raw cell value, unused.
     * @param record - The study row the actions apply to.
     * @returns The action buttons for the row, or a skeleton while data loads.
     */
    const actionFormatter = (_: string, record: FormattedStudy) => {
      if (state.isLoadedData) {
        return isCompleted ? (
          <Space>
            <MoreOptionsPopover
              data={record}
              supportFacilities={supportFacilities}
              onClickCopyStudy={onClickCopyStudy}
              toggleDisplayConfirmDeleteStudiesModal={toggleDisplayConfirmDeleteStudiesModal}
            />
            <CustomTooltip title={t`Open study management`} placement="bottomRight">
              <Button
                type="text"
                onClick={() => {
                  navigate(`/studies/${record.id}`);
                }}
              >
                <img src={studyTableIcon} alt="studyTableIcon" />
              </Button>
            </CustomTooltip>
          </Space>
        ) : (
          <Space align="center" style={{ width: '100%', justifyContent: 'center' }}>
            <CustomTooltip title={t`Restore study`} placement="bottomRight">
              <Button
                type="text"
                onClick={() => {
                  onClickRestoreButton(record.id, record.studyFid);
                }}
              >
                <img src={restoreIcon} alt="restoreIcon" />
              </Button>
            </CustomTooltip>
          </Space>
        );
      }

      return <Skeleton count={1} />;
    };
    /**
     * Assembles the antd column set for the current studies tab. Opens with the sortable Study ID column bound to studyFidFormatter and convertSortOrder, inserts ETA report date only for the completed tab, pushes stop time, patient return visit, duration, initial duration, patient name, patient ID, device ID and study type through their formatters, adds the facility column when state.isShowFacilityName, and ends with a right-fixed action column whose width varies by tab.
     *
     * @returns The columns for the current tab.
     */
    const generateColumns = () => {
      const columns: TableColumn[] = [
        {
          title: t`Study ID`,
          dataIndex: 'studyFid',
          width: 120,
          fixed: 'left',
          sorter: true,
          sortOrder: convertSortOrder(undefined, filterObject.current) ?? undefined,
          sortIcon: ({ sortOrder }: { sortOrder: string }) => renderSortIcon(sortOrder),
          render: (text: number, record: FormattedStudy) =>
            studyFidFormatter({
              data: text,
              record,
              extraData: {
                isDeleted,
                toggleDisplayDrawer: toggleDisplayDrawerProp,
                isLoadedData: state.isLoadedData,
              },
            }),
        },
      ];

      if (isCompleted) {
        columns.push({
          title: t`ETA report date`,
          dataIndex: 'etaReportDate',
          width: 150,
          render: (text, record) =>
            etaReportDateFormatter({
              data: text,
              record,
              extraData: {
                isLoadedData: state.isLoadedData,
              },
            }),
        });
      }

      columns.push(
        {
          title: `${t`Stop time`}`,
          dataIndex: 'stop',
          width: 140,
          render: (text, record) =>
            stopTimeFormatter({
              data: text,
              record,
              extraData: {
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Patient return visit`}`,
          dataIndex: 'reportDueDate',
          width: 150,
          render: (text, record) =>
            patientReturnVisitFormatter({
              record,
              extraData: {
                isOngoing,
                isFlaggedStudiesTab,
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Duration`}`,
          dataIndex: 'duration',
          width: 120,
          render: (text, record) =>
            durationFormatter({
              record,
              extraData: {
                isCompleted,
                isDeleted,
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Initial duration`}`,
          dataIndex: 'initialDurationOfStudy',
          width: 120,
          render: (text, record) =>
            initDurationFormatter({
              record,
              extraData: {
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Patient name`}`,
          key: 'name',
          dataIndex: ['patient', 'name'],
          width: 200,
          render: (text, record) =>
            patientNameFormatter({
              data: text,
              record,
              extraData: {
                toggleDisplayDrawer: toggleDisplayDrawerProp,
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Patient ID`}`,
          key: 'patientId',
          dataIndex: ['patient', 'patientId'],
          width: 120,
          render: (text) =>
            cellFormatter({
              data: text,
              extraData: {
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Device ID`}`,
          key: 'deviceId',
          dataIndex: ['device', 'deviceId'],
          width: 135,
          render: (text, record) =>
            deviceIdFormatter({
              data: text,
              record,
              extraData: {
                toggleDisplayDrawer: toggleDisplayDrawerProp,
                isLoadedData: state.isLoadedData,
              },
            }),
        },
        {
          title: `${t`Study type`}`,
          dataIndex: 'studyType',
          width: 185,
          render: (text, record) =>
            studyTypeFormatter({
              data: text,
              record,
              extraData: {
                isCompleted,
                isShowFacilityName: state.isShowFacilityName,
                isLoadedData: state.isLoadedData,
              },
            }),
        },
      );

      if (state.isShowFacilityName) {
        columns.push({
          title: `${t`Facility`}`,
          dataIndex: ['facility', 'name'],
          key: 'facility.name',
          width: 200,
          render: (text) =>
            cellFormatter({
              data: text,
              extraData: {
                isLoadedData: state.isLoadedData,
              },
            }),
        });
      }

      columns.push({
        dataIndex: 'action',
        title: t`Actions`,
        fixed: 'right',
        width: isCompleted ? 88 : isDeleted ? 72 : undefined,
        render: actionFormatter,
      });

      return columns;
    };

    return (
      (isActive || state.isLoadedData) && (
        <>
          {(isOngoing || isUnassigned) && (
            <>
              <ConfirmModal
                className="abort-study-confirm-modal"
                isOpen={state.openConfirmAbortStudyModal}
                title={t`Abort study`}
                question={t`This action cannot be undone. Are you sure you want to abort this study? `}
                leftButtonName="Cancel"
                onClickLeftButton={handleCancelClick}
                rightButtonClassName="abort-btn"
                rightButtonName={t`Abort`}
                buttonRightStyle="danger"
                buttonRightType="primary"
                onClickRightButton={handleRightButtonClick}
                isSaving={state.isCallingAbortStudy}
                destroyOnClose
              />
              <ConfirmModal
                className="reference-code-modal"
                isOpen={state.isOpenResendModal}
                onClickLeftButton={() => {
                  toggleDisplayResendModal();
                }}
                onClickRightButton={resendReferenceCode}
                leftButtonName={t`Cancel`}
                rightButtonName={hasSentRefCode ? t`Resend` : t`Send`}
                title={hasSentRefCode ? t`Resend reference code` : t`Send reference code`}
                question={generateResendReferenceCodeQuestion()}
                isRedBtn={false}
                isSaving={state.isSaving}
              />
              <ConfirmModal
                className="reference-code-modal"
                isOpen={state.isOpenTooManyRequestModal}
                isRedBtn={false}
                rightButtonName={t`Okay`}
                onClickRightButton={() => {
                  setState({ isOpenTooManyRequestModal: false });
                }}
                title={t`Too many requests`}
                question={state.tooManyRequestMessage}
              />
              <ConfirmModal
                isOpen={state.openStopLiveEcgModal}
                title={t`Live ECG is stopped`}
                question={t`The live ECG has stopped because you have been inactive for more than 10 minutes.`}
                leftButtonName={t`Close`}
                onClickLeftButton={() => {
                  displayStopLiveEcgModal(false);
                }}
                rightButtonName={t`Resume`}
                onClickRightButton={handleResumeStreaming}
                destroyOnClose
              />
              <ConfirmActivityModal
                isOpen={state.openConfirmActivityModal}
                onClickRightButton={onClickConfirmActivityModal}
                handleStopStreaming={handleStopStreaming}
              />
              <ViewLiveEcgModal
                ref={viewLiveEcgModalRef}
                className="view-live-ecg-modal"
                title={t`View live ECG`}
                open={state.openLiveEcgModal}
                study={state.displayedLiveEcgModalStudy}
                onCancel={() => {
                  displayViewLiveEcg(false);
                }}
                displayConfirmActivityModal={displayConfirmActivityModal}
              />

              <BiodirectInfoDrawer
                study={state.displayedBiodirectStudy}
                onClose={() => {
                  setState({ displayedBiodirectStudy: null });
                }}
              />
            </>
          )}
          {(isOngoing || isFlaggedStudiesTab) && (
            <>
              <ConfirmModal
                className="abort-study-confirm-modal"
                isOpen={state.isOpenConfirmStopStudyModal}
                title={t`Stop study`}
                question={t`This action cannot be undone. Are you sure you want to stop this study?`}
                leftButtonName="Cancel"
                onClickLeftButton={toggleDisplayConfirmStopStudyModal}
                rightButtonClassName="abort-btn"
                rightButtonName={t`Stop`}
                buttonRightStyle="danger"
                buttonRightType="primary"
                onClickRightButton={stopStudy}
                isSaving={state.isStopingStudyModal}
                destroyOnClose
              />

              <ConfirmModal
                isOpen={state.isOpenUnableStopStudyModal}
                title={t`Unable to Stop Study`}
                question={t`You can’t stop this study right now because there are pending updates still being processed. Please make sure the device is online to receive the updates, then try again.`}
                rightButtonName={t`Got it`}
                buttonRightType="primary"
                onClickRightButton={() => {
                  setState({ isOpenUnableStopStudyModal: false });
                }}
                destroyOnClose
              />

              <DisclaimerModal
                isOpen={state.isOpenDisclaimerModal}
                rightButtonName={t`Okay`}
                onClickRightButton={() => {
                  toggleDisplayDisclaimerModal();
                }}
                study={state.displayedDisclaimerModalStudy}
              />
            </>
          )}
          {isCompleted && (
            <ConfirmModal
              className="custom-confirm-modal"
              isOpen={isOpenConfirmDeleteStudiesModal}
              onClickLeftButton={() => {
                toggleDisplayConfirmDeleteStudiesModal(checkedStudyIds, false, false);
              }}
              onClickRightButton={deleteStudies}
              leftButtonName={t`Cancel`}
              rightButtonName={t`Delete`}
              title={t`Delete study`}
              question={`${t`Deleted studies can be found in “Deleted” tab. Are you sure you want to delete`} ${checkedStudyIds.length > 1 ? t`these studies` : t`this study`}?`}
              isSaving={isDeletingStudies}
            />
          )}
          {isFlaggedStudiesTab && (
            <SolutionsDrawer
              isOpen={state.isOpenSolutionsModal}
              onClickClose={() => {
                toggleDisplaySolutionsDrawer();
              }}
              data={state.displayedSolutionStudy}
            />
          )}
          <ConfirmModal
            isOpen={!!state.isConfirmGetECG}
            onClickLeftButton={() => {
              setState({ isConfirmGetECG: null });
            }}
            onClickRightButton={() => {
              // Non-null: this button only renders/fires while isOpen (!!state.isConfirmGetECG) is true.
              onClickGetECG(state.isConfirmGetECG!);
            }}
            leftButtonName={t`No`}
            rightButtonName={t`Yes`}
            title={t`Get ECG`}
            question={t`Proceed to get an ECG strip?`}
          />
          <UploadStatusModal
            isOpen={state.isOpenUploadStatusModal}
            rightButtonName={t`Got It`}
            onClickRightButton={() => {
              toggleDisplayUploadStatusModal();
            }}
            study={state.displayedUploadStatusModalStudy}
          />
          {isFlaggedStudiesTab && (
            <>
              <CommonQuestionsDrawer
                visible={state.isVisibleCommonQuestionsDrawer}
                title={t`Common Questions`}
                handleClickCloseButton={toggleDisplayDrawer}
              />

              <CustomTooltip
                title={
                  <div className="common-question-container">
                    <div className="content">
                      <div>
                        <div>{t`What is Flagged Studies`}</div>
                        <div>{t`Click here to learn more.`}</div>
                      </div>
                      <img src={closeWhiteIcon} alt="closeWhiteIcon" onClick={onClickConfirm} />
                    </div>
                    <div>
                      <Checkbox
                        id="flagged-studies-do-not-show-checkbox"
                        name="doNotShowFlaggedStudies"
                        className="check-box"
                        checked={state.isCheck}
                        onChange={onChangeCheckbox}
                      >
                        {t`Do not show this again`}
                      </Checkbox>
                    </div>
                  </div>
                }
                align={{
                  offset: [0, -10],
                }}
                classNames={{ root: 'common-question-popup' }}
                getPopupContainer={(ele) => ele}
                trigger="click"
                open={state.isOpenNotiPopover}
                placement="topRight"
              >
                <CustomButton
                  size="middle"
                  icon={<ReactSVG src={lightbulbIcon} />}
                  className="common-questions-btn animated zoomIn"
                  id="common-questions-btn"
                  onClick={handleClickCommonQuestions}
                  ariaLabel={t`Common questions`}
                />
              </CustomTooltip>
            </>
          )}
          {isUnassigned && (
            <AssignDeviceDrawer
              visible={state.visibleAssignDeviceDrawer}
              handleClickCloseButton={() => {
                setState({ visibleAssignDeviceDrawer: false });
              }}
              study={state.shouldAssignDeviceStudy as any}
              handleAssignDeviceDrawerClick={handleAssignDeviceDrawerClick}
            />
          )}

          {isActive && (
            <div
              className={classNames(
                !state.isLoadedData &&
                  (facilitySelection.userSupportFacilities()?.length > 0 ||
                    supportFacilities?.length > 0)
                  ? 'relative'
                  : '',
              )}
            >
              <SkeletonGradientBackground isShow={!state.isLoadedData} />

              {shouldDisplayStudiesMatch && (
                <div className="studies-match-container">
                  <div className="text">
                    {!state.studies.length ? t`No study matching` : t`Studies match`}
                    {' "'}
                    {searchByValue === 'studyFid'
                      ? multiStudyFid?.length > 0
                        ? multiStudyFid.join(', ')
                        : searchValue !== ''
                          ? searchValue
                          : ''
                      : searchValue !== '' && searchByValue !== 'studyFid'
                        ? searchValue
                        : ''}
                    {'" '}
                  </div>

                  <div className="studies-match-container-right ">
                    {isCompleted && (
                      <>
                        <div className="switch-group">
                          <div className="text">{t`Include aborted study`}</div>
                          <Switch
                            className="switch-btn"
                            checked={state.isCheckedAbortedStudy}
                            disabled={state.isChangingFacility}
                            onChange={onChangeCheckboxAbortedStudy}
                            id="include-aborted-study"
                            aria-label={t`Include aborted study`}
                          />
                        </div>
                        <div className="divider" />
                      </>
                    )}

                    <NormalButton
                      id="clear-btn"
                      buttonName={t`Clear`}
                      className="btn-clear"
                      isText
                      onClick={onClickClear}
                    />
                  </div>
                </div>
              )}

              {shouldDisplaySelectedStudies && (
                <div
                  className={classNames('list-selected-study', {
                    '--has-studies-match': shouldDisplayStudiesMatch,
                  })}
                >
                  <div className="selected-studies-count">
                    <img
                      className="clear-selected-studies-btn"
                      src={CloseIcon}
                      alt="Close icon"
                      onClick={onClickClearSelectStudies}
                    />
                    <div>
                      <strong>{checkedStudyIds.length}</strong>{' '}
                      {checkedStudyIds.length > 1 ? 'studies' : 'study'} selected
                    </div>
                  </div>
                  <CustomButton
                    id="delete-selected-btn"
                    onClick={() => {
                      toggleDisplayConfirmDeleteStudiesModal(checkedStudyIds, true);
                    }}
                    style="danger"
                    type="tertiary"
                    size="small"
                  >
                    {t`Delete selected study`}
                  </CustomButton>
                </div>
              )}

              {(state.studies.length || (!state.studies.length && !state.isLoadedData)) &&
              (facilitySelection.userSupportFacilities()?.length > 0 ||
                supportFacilities?.length > 0) ? (
                isOngoing || isFlaggedStudiesTab ? (
                  <div
                    className={classNames('study-card-container', {
                      '--has-studies-match': shouldDisplayStudiesMatch,
                      '--has-selected-studies': shouldDisplaySelectedStudies,
                      '--has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
                    })}
                  >
                    {_.map(
                      state.isLoadedData
                        ? state.studies
                        : (state.skeletonStudies as unknown as FormattedStudy[]),
                      (study: FormattedStudy) => (
                        <StudyCard
                          ref={
                            ((el: any) => {
                              (studyCardRefs.current as any)[study.id] = el;
                            }) as any
                          }
                          key={study.id}
                          isOngoing={isOngoing}
                          isFlaggedStudiesTab={isFlaggedStudiesTab}
                          study={study}
                          supportFacilities={supportFacilities}
                          isLoadedData={state.isLoadedData}
                          handleClickDeviceId={() => {
                            toggleDisplayDrawerProp(study, t`Device information`);
                          }}
                          handleClickStudyId={() => {
                            toggleDisplayDrawerProp(study, t`Study information`);
                          }}
                          handleClickPatientName={() => {
                            toggleDisplayDrawerProp(study, t`Patient information`);
                          }}
                          handleClickCopy={() => {
                            onClickCopyStudy(study.id);
                          }}
                          handleViewBiodirectInfoClick={() => {
                            setState({ displayedBiodirectStudy: study });
                          }}
                          handleGetEcgClick={() => {
                            setState({ isConfirmGetECG: study.id });
                          }}
                          handleGoToStudyManagement={() => {
                            navigate(`/studies/${study.id}`);
                          }}
                          handleClickStop={() => {
                            toggleDisplayConfirmStopStudyModal(
                              study.studyFid,
                              study?.pendingCommand?.command,
                            );
                          }}
                          handleAbortClick={() => {
                            handleAbortClick(study);
                          }}
                          onUpdateMarkAddressed={onUpdateMarkAddressed}
                          isShowFacilityName={state.isShowFacilityName}
                          toggleDisplaySolutionsDrawer={toggleDisplaySolutionsDrawer}
                          toggleDisplayDisclaimerModal={toggleDisplayDisclaimerModal}
                          toggleDisplayUploadStatusModal={toggleDisplayUploadStatusModal}
                          toggleDisplayResendModal={toggleDisplayResendModal}
                          displayViewLiveEcg={displayViewLiveEcg}
                          handleGetLastEvaluationEvent={handleGetLastEvaluationEvent}
                        />
                      ),
                    )}

                    <BottomScrollListener offset={500} onBottom={useBottomScrollListener} />

                    <div className="loading-more">
                      {state.isLoadingMore && (
                        <>
                          <Spin
                            indicator={
                              <LoadingOutlined
                                spin
                                style={{
                                  fontSize: 24,
                                  color: '#2265c6',
                                }}
                              />
                            }
                          />
                          <div className="loading-more__text">{t`Loading more...`}</div>
                        </>
                      )}
                    </div>
                  </div>
                ) : isUnassigned ? (
                  <div
                    className={classNames('unassigned-study-card-container', {
                      '--has-studies-match': shouldDisplayStudiesMatch,
                    })}
                  >
                    {_.map(
                      state.isLoadedData
                        ? state.studies
                        : (state.skeletonStudies as unknown as FormattedStudy[]),
                      (study: FormattedStudy) => (
                        <UnassignedStudyCard
                          key={study.id}
                          study={study}
                          supportFacilities={supportFacilities}
                          isLoadedData={state.isLoadedData}
                          isShowFacilityName={state.isShowFacilityName}
                          handleClickStudyId={() => {
                            toggleDisplayDrawerProp(study, t`Study information`);
                          }}
                          handleClickPatientName={() => {
                            toggleDisplayDrawerProp(study, t`Patient information`);
                          }}
                          handleAbortClick={() => {
                            handleAbortClick(study);
                          }}
                          handleClickCopy={() => {
                            onClickCopyStudy(study.id);
                          }}
                          handleGoToStudyManagement={() => {
                            navigate(`/studies/${study.id}`);
                          }}
                          handleAssignDeviceClick={() => {
                            handleAssignDeviceClick(study);
                          }}
                        />
                      ),
                    )}

                    <BottomScrollListener offset={500} onBottom={useBottomScrollListener} />

                    <div className="loading-more">
                      {state.isLoadingMore && (
                        <>
                          <Spin
                            indicator={
                              <LoadingOutlined
                                spin
                                style={{
                                  fontSize: 24,
                                  color: '#2265c6',
                                }}
                              />
                            }
                          />
                          <div className="loading-more__text">{t`Loading more...`}</div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <TableComponent
                    className={classNames('studies-table', {
                      '--has-studies-match': shouldDisplayStudiesMatch,
                      '--has-selected-studies': shouldDisplaySelectedStudies,
                    })}
                    data={state.studies || []}
                    columns={generateColumns()}
                    isLoading={!state.isLoadedData}
                    handleTableChange={handleTableChange}
                    currentPage={state.studiesPage}
                    pageSize={state.sizePerPage}
                    isEndOfData={state.isEndOfData}
                    rowSelection={
                      isCompleted
                        ? {
                            selectedRowKeys: checkedStudyIds,
                            onSelect: (record: FormattedStudy, selected: boolean) => {
                              onSelectStudy(selected, record);
                            },
                            onSelectAll: (
                              selected: boolean,
                              selectedRows: FormattedStudy[],
                              changeRows: FormattedStudy[],
                            ) => {
                              onSelectAllStudies(selected, changeRows);
                            },
                            getCheckboxProps: (record: FormattedStudy) => ({
                              disabled: record.status === STUDY_STATUS.ABORTED, // *: Column configuration not to be checked
                            }),
                          }
                        : undefined
                    }
                    scrollX={1633}
                  />
                )
              ) : (
                <div
                  className={classNames(
                    'no-data-wrapper',
                    (!!filterObject.current.studyFid ||
                      !!filterObject.current.deviceId ||
                      !!filterObject.current.patientName) &&
                      '--searching',
                  )}
                >
                  <div className="no-data">
                    <NoDataComponent
                      isSearching={
                        !!filterObject.current.studyFid ||
                        !!filterObject.current.deviceId ||
                        !!filterObject.current.patientName
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )
    );
  },
);

export default StudiesTable;
